import React from 'react'
import PropTypes from 'prop-types'
import * as Classes from '@blueprintjs/core/lib/cjs/common/classes'
import { Button } from '@blueprintjs/core/lib/cjs/components/button/buttons'
import { Dialog } from '@blueprintjs/core/lib/cjs/components/dialog/dialog'
import { DEFAULT_DELETE_MODAL_TEXTS } from 'src/routes/Runbooks/RunbooksTemplates/TemplateActionList/constants'
import styles from './deleteTemplateModal.module.scss'

/**
 * Generic confirmation dialog for deleting an entity (template or other resource).
 * Supports single and bulk delete modes.
 * Default labels and text values are sourced from DEFAULT_DELETE_MODAL_TEXTS.
 * @param {boolean} isOpen - Controls visibility of the modal
 * @param {object} [template] - Template object (fallback entity if not provided)
 * @param {object} [entity] - Generic entity object used when template is not available
 * @param {string|null} [mode] - Modal mode. If 'bulk', shows bulk delete warning UI
 * @param {boolean} [isDeleting] - Indicates loading state during delete operation
 * @param {string} [title] - Modal title (defaults from DEFAULT_DELETE_MODAL_TEXTS)
 * @param {string} [itemLabel] - Label used in confirmation message (defaults from constants)
 * @param {string} [warningMessage] - Custom warning message shown in body
 * @param {string} [confirmLabel] - Label for confirm button (defaults from constants)
 * @param {string} [cancelLabel] - Label for cancel button (defaults from constants)
 * @param {function} onCancel - Callback when modal is closed or cancelled
 * @param {function} onConfirm - Callback when delete is confirmed
 */
const DeleteTemplateModal = ({
    isOpen,
    template,
    mode = null,
    entity,
    isDeleting,
    title,
    itemLabel,
    warningMessage,
    confirmLabel,
    cancelLabel,
    onCancel,
    onConfirm,
}) => {
    const targetEntity = template || entity
    const resolvedItemLabel = itemLabel || DEFAULT_DELETE_MODAL_TEXTS.itemLabel
    const resolvedTitle = title || DEFAULT_DELETE_MODAL_TEXTS.title
    const resolvedConfirmLabel =
        confirmLabel || DEFAULT_DELETE_MODAL_TEXTS.confirmLabel
    const resolvedCancelLabel =
        cancelLabel || DEFAULT_DELETE_MODAL_TEXTS.cancelLabel
    const isBulk = mode === 'bulk'

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onCancel}
            title={resolvedTitle}
            canEscapeKeyClose={!isDeleting}
            canOutsideClickClose={!isDeleting}
        >
            <div className={Classes.DIALOG_BODY}>
                {isBulk ? (
                    <p>{warningMessage}</p>
                ) : (
                    targetEntity && (
                        <>
                            <p>
                                Are you sure you want to delete{' '}
                                {resolvedItemLabel} <b>{targetEntity.name}</b>?
                            </p>
                            <p>
                                {warningMessage ||
                                    `Deleting a ${resolvedItemLabel} cannot be undone and all data will be lost.`}
                            </p>
                        </>
                    )
                )}
            </div>
            <div className={Classes.DIALOG_FOOTER}>
                <div className={styles.footerActions}>
                    <Button
                        onClick={onCancel}
                        disabled={isDeleting}
                        className={styles.cancelButton}
                    >
                        {resolvedCancelLabel}
                    </Button>
                    <Button
                        intent="danger"
                        onClick={onConfirm}
                        loading={isDeleting}
                        disabled={isDeleting}
                        className={styles.deleteButton}
                    >
                        {resolvedConfirmLabel}
                    </Button>
                </div>
            </div>
        </Dialog>
    )
}

DeleteTemplateModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    template: PropTypes.object,
    entity: PropTypes.object,
    mode: PropTypes.string,
    isDeleting: PropTypes.bool,
    title: PropTypes.string,
    itemLabel: PropTypes.string,
    warningMessage: PropTypes.string,
    confirmLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
}

export default DeleteTemplateModal
